import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button as MuiButton,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import { handleAuth } from "../../../components/Login/Auth.jsx";
import AxiosInstance from "../../AxiosInstance.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LoaderComponent,
  WhiteLoaderComponent,
} from "../../../components/Icon/Index.jsx";
import BlueButton from "../../../components/Button/BlueButton.jsx";
import WhiteButton from "../../../components/Button/WhiteButton.jsx";
import showToast from "../../../components/Toast/Toster.jsx";
import { useUserType } from "../../../contexts/UserTypeContext";
import {
  JobberPagination,
  JobberSearch,
  JobberTable,
  JobberListCard,
  JobberStatusBadge,
} from "../../../components/MuiTable";
import { NoDataFound } from "../../../components/ContractComponent/Index";
import SearchInput from "../../../components/InputFields/SearchInput.jsx";
import DatePicker from "../../../components/DatePicker/DatePicker";
import moment from "moment-timezone";
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  GetApp as DownloadAllIcon,
  Description as DocumentIcon,
  Person as PersonIcon,
  AccessTime as ClockIcon,
  Payment as PaymentIcon,
  Email as MailIcon,
  SettingsInputAntenna as WebhookIcon,
  People as PeopleIcon,
  Chat as SlackIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIconMain,
} from "@mui/icons-material";
import "./style.css";

/** Row action tiles (view / download) — design system blues + green accent */
const LOG_VIEW_ACTION_BG = "#EFF6FF";
const LOG_VIEW_ACTION_BORDER = "1px solid rgba(37, 99, 235, 0.22)";
const LOG_VIEW_ICON = "#2563EB";
const LOG_DOWNLOAD_ACTION_BG = "#ECFDF5";
const LOG_DOWNLOAD_ACTION_BORDER = "1px solid rgba(5, 150, 105, 0.22)";
const LOG_DOWNLOAD_ICON = "#059669";
const LOG_ACTION_DISABLED_BG = "#F3F4F6";
const LOG_ACTION_DISABLED_BORDER = "1px solid #E5E7EB";

/** Mobile Jobber card actions — same pattern as Permission / Plan (`customer-list-actions-mobile`) */
const serverLogsMobileActionBtnBase = {
  width: "100%",
  minWidth: 0,
  alignSelf: "stretch",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.7rem",
  borderRadius: "8px",
  minHeight: 32,
  px: 0.5,
  py: 0.375,
  lineHeight: 1.15,
  boxShadow: "none",
  whiteSpace: "nowrap",
  justifyContent: "center",
  "& .MuiButton-startIcon": { mr: 0.25, ml: -0.1 },
};

/** Toolbar row: align search + date + icon (matches `JobberSearch` fieldHeight) */
const SERVER_LOGS_TOOLBAR_H = 40;

/** If a string value looks like JSON, parse it so pretty-print nests (e.g. responseBody). */
const expandEmbeddedJsonStrings = (value) => {
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => expandEmbeddedJsonStrings(item));
  }
  const out = {};
  for (const [key, val] of Object.entries(value)) {
    if (typeof val === "string") {
      const t = val.trim();
      const looksLikeObj = t.startsWith("{") && t.endsWith("}");
      const looksLikeArr = t.startsWith("[") && t.endsWith("]");
      if (looksLikeObj || looksLikeArr) {
        try {
          const inner = JSON.parse(val);
          out[key] = expandEmbeddedJsonStrings(inner);
        } catch {
          out[key] = val;
        }
      } else {
        out[key] = val;
      }
    } else if (val !== null && typeof val === "object") {
      out[key] = expandEmbeddedJsonStrings(val);
    } else {
      out[key] = val;
    }
  }
  return out;
};

/** Dash-only separator lines in log files (e.g. auth logs). */
const DOWNLOAD_LOG_SEPARATOR = /^-{20,}$/;

/**
 * One physical line → timestamp/info head + pretty-printed JSON when `{...}` parses.
 * Preserves original order (no reverse) for downloads.
 */
const formatSingleLogLineForDownload = (rawLine) => {
  const line = rawLine.replace(/\r/g, "");
  const brace = line.indexOf("{");
  if (brace === -1) {
    return line;
  }
  const head = line.slice(0, brace).trimEnd();
  const tail = line.slice(brace);
  try {
    const parsed = JSON.parse(tail);
    const expanded = expandEmbeddedJsonStrings(parsed);
    const pretty = JSON.stringify(expanded, null, 2);
    return `${head}\n${pretty}`;
  } catch {
    return line;
  }
};

/**
 * Full file: keep separator lines; pretty-print JSON entries; trim excessive blank runs.
 */
const formatLogFileForDownload = (rawContent) => {
  if (rawContent == null || rawContent === "") {
    return "";
  }
  const chunks = [];
  for (const rawLine of String(rawContent).split(/\n/)) {
    const line = rawLine.replace(/\r/g, "");
    const trimmed = line.trim();
    if (DOWNLOAD_LOG_SEPARATOR.test(trimmed)) {
      chunks.push("", trimmed, "");
      continue;
    }
    if (!trimmed) {
      chunks.push("");
      continue;
    }
    chunks.push(formatSingleLogLineForDownload(line));
  }
  return `${chunks.join("\n").replace(/\n{3,}/g, "\n\n").trim()}\n`;
};

/**
 * Maps log `type` strings to Jobber status pill tones (§6 in MuiTable style).
 * Keeps labels as returned by the API; only visual styling changes.
 */
const getLogTypeBadgeTone = (typeRaw) => {
  const key = String(typeRaw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  switch (key) {
    case "auth":
    case "users":
    case "admin":
      return "active";
    case "error":
      return "overdue";
    case "payments":
      return "paid";
    case "slack-api":
    case "slackapi":
      return "change";
    case "salt-api":
    case "saltapi":
      return "scheduled";
    case "mail":
    case "email":
      return "scheduled";
    case "webhook":
      return "converted";
    default:
      return "neutral";
  }
};

const ServerLogs = () => {
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const isServerLogsMobileCard = useMediaQuery("(max-width: 775px)");

  // Define colors based on donation mode
  const colors = {
    bgClass: isDonationMode ? "bg-donor-color" : "bg-blue-color",
    borderClass: isDonationMode ? "border-donor-color" : "border-blue-color",
    textClass: isDonationMode ? "text-donor-color" : "text-blue-color",
    borderColor: isDonationMode ? "#29B3E2" : "#0f2b46",
  };

  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = import.meta.env.VITE_BASE_API;
  
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed for pagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState(moment().tz("America/New_York").format("MM/DD/YYYY"));
  const [dateFormat] = useState("MM/DD/YYYY");
  const [sortField, setSortField] = useState("modifiedTime");
  const [sortOrder, setSortOrder] = useState("desc");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingLog, setViewingLog] = useState(null);
  const [viewingContent, setViewingContent] = useState("");
  const [viewingLoading, setViewingLoading] = useState(false);
  const [logSearch, setLogSearch] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const logContentRef = useRef(null);

  useEffect(() => {
    const fetchTokenData = async () => {
      try {
        await handleAuth(navigate, location);
        fetchLogs();
      } catch (error) {
        console.error("Error: ", error.message);
        setLoading(false);
      }
    };
    fetchTokenData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== undefined) {
        fetchLogs();
      }
    }, search ? 500 : 0); // 500ms delay for search, immediate for other changes

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, selectedDate, search, sortField, sortOrder]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      // Convert MM/DD/YYYY to MM/DD/YYYY for API
      const apiDate = selectedDate.replace(/-/g, "/");
      
      const response = await AxiosInstance.get(`/v1/server-logs/list`, {
        params: {
          page: page + 1, // API expects 1-indexed page
          pageSize: rowsPerPage,
          date: apiDate,
          type: "All Types",
          search: search || "",
          sortField: sortField || "modifiedTime",
          sortOrder: sortOrder || "desc",
        },
      });

      if (response?.data?.statusCode === 200) {
        setLogs(response.data.logs || []);
        setTotalCount(response.data.totalCount || 0);
      } else if (response?.data?.statusCode === 204) {
        // No logs found for this date/filter
        setLogs([]);
        setTotalCount(0);
      } else {
        showToast.error(response?.data?.message || "Failed to load logs");
        setLogs([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      showToast.error("Failed to load server logs");
    } finally {
      setLoading(false);
    }
  };

  const handleViewLog = async (log) => {
    try {
      setViewingLog(log);
      setViewDialogOpen(true);
      setViewingLoading(true);
      setViewingContent("");
      setOriginalContent("");
      setLogSearch("");
      
      const response = await AxiosInstance.get(`/v1/server-logs/view`, {
        params: {
          fileName: log.fileName,
          directory: log.directory,
        },
      });

      if (response?.data?.statusCode === 200) {
        const content = response.data.content || "";
        // Reverse the log lines so newest entries appear first
        const lines = content.split('\n');
        const reversedContent = lines.reverse().join('\n');
        setOriginalContent(reversedContent);
        setViewingContent(reversedContent);
        
        // Auto-scroll to top after a brief delay to ensure content is rendered
        setTimeout(() => {
          if (logContentRef.current) {
            logContentRef.current.scrollTop = 0;
          }
        }, 100);
      } else {
        showToast.error(response?.data?.message || "Failed to load log content");
        setViewingContent("Error loading log file content.");
        setOriginalContent("Error loading log file content.");
      }
    } catch (error) {
      console.error("Error viewing log:", error);
      showToast.error("Failed to load log content");
      setViewingContent("Error loading log file content.");
      setOriginalContent("Error loading log file content.");
    } finally {
      setViewingLoading(false);
    }
  };

  const handleDownloadLog = async (log) => {
    const triggerBlobDownload = (blobData, fileName) => {
      const url = window.URL.createObjectURL(new Blob([blobData]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    };

    try {
      const viewRes = await AxiosInstance.get(`/v1/server-logs/view`, {
        params: {
          fileName: log.fileName,
          directory: log.directory,
        },
      });

      if (viewRes?.data?.statusCode === 200) {
        const raw = viewRes.data.content ?? "";
        const formatted = formatLogFileForDownload(raw);
        const blob = new Blob([formatted], { type: "text/plain;charset=utf-8" });
        triggerBlobDownload(blob, log.fileName);
        showToast.success("Log file downloaded successfully");
        return;
      }

      console.warn("server-logs/view failed for formatted download, falling back to raw file:", viewRes?.data);
    } catch (e) {
      console.warn("Formatted log download failed, falling back to raw file:", e);
    }

    try {
      const response = await AxiosInstance.get(`/v1/server-logs/download`, {
        params: {
          fileName: log.fileName,
          directory: log.directory,
        },
        responseType: "blob",
      });
      triggerBlobDownload(response.data, log.fileName);
      showToast.success("Log file downloaded successfully");
    } catch (error) {
      console.error("Error downloading log:", error);
      showToast.error("Failed to download log file");
    }
  };


  const handleDatePickerChange = (e) => {
    if (e?.target?.value) {
      // DatePicker returns YYYY-MM-DD format, convert to MM/DD/YYYY
      const formattedDate = moment(e.target.value).format("MM/DD/YYYY");
      setSelectedDate(formattedDate);
      setPage(0);
    }
  };

  const handleDownloadAll = async () => {
    try {
      // Convert MM/DD/YYYY to MM/DD/YYYY for API
      const apiDate = selectedDate.replace(/-/g, "/");
      
      const response = await AxiosInstance.get(`/v1/server-logs/download-all`, {
        params: {
          date: apiDate,
        },
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `server-logs-${moment().format("YYYY-MM-DD")}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("All logs downloaded successfully");
    } catch (error) {
      console.error("Error downloading all logs:", error);
      showToast.error("Failed to download logs");
    }
  };

  const getLogIcon = (iconType) => {
    const iconMap = {
      person: <PersonIcon fontSize="small" />,
      admin: <PeopleIcon fontSize="small" />,
      document: <DocumentIcon fontSize="small" />,
      clock: <ClockIcon fontSize="small" />,
      payments: <PaymentIcon fontSize="small" />,
      mail: <MailIcon fontSize="small" />,
      webhook: <WebhookIcon fontSize="small" />,
      users: <PeopleIcon fontSize="small" />,
      slack: <SlackIcon fontSize="small" />,
    };
    return iconMap[iconType] || <DocumentIcon fontSize="small" />;
  };

  const getTypeColor = (typeColor) => {
    const colorMap = {
      purple: "#9c27b0",
      "light-blue": "#03a9f4",
      orange: "var(--yellow)",
      "light-green": "#8bc34a",
      teal: "#009688",
      "light-gray": "var(--text-muted)",
    };
    return colorMap[typeColor] || "var(--text-muted)";
  };

  // Helper function to highlight search term in text
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) {
      return text;
    }

    // Escape special regex characters in search term
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
    
    // Split text by search term and wrap matches
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      // Check if this part matches the search term (case-insensitive)
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <mark
            key={index}
            style={{
              backgroundColor: '#ffeb3b',
              color: '#000',
              padding: '2px 0',
              borderRadius: '2px',
            }}
          >
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  /** Presentation-only helpers for the view dialog (filtering / API flow unchanged). */
  const LOG_JSON_COLORS = {
    key: "#9CDCFE",
    string: "#CE9178",
    number: "#B5CEA8",
    keyword: "#569CD6",
    punctuation: "#D4D4D4",
  };

  const JSON_TOKEN_RE =
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g;

  const highlightJsonTokens = (jsonStr) => {
    const parts = [];
    let last = 0;
    let m;
    const re = new RegExp(JSON_TOKEN_RE.source, "g");
    while ((m = re.exec(jsonStr)) !== null) {
      if (m.index > last) {
        parts.push(
          <span key={`t-${parts.length}`} style={{ color: LOG_JSON_COLORS.punctuation }}>
            {jsonStr.slice(last, m.index)}
          </span>
        );
      }
      const match = m[0];
      let color = LOG_JSON_COLORS.number;
      if (/^"/.test(match)) {
        color = /:\s*$/.test(match) ? LOG_JSON_COLORS.key : LOG_JSON_COLORS.string;
      } else if (/true|false|null/.test(match)) {
        color = LOG_JSON_COLORS.keyword;
      }
      parts.push(
        <span key={`t-${parts.length}`} style={{ color }}>
          {match}
        </span>
      );
      last = re.lastIndex;
    }
    if (last < jsonStr.length) {
      parts.push(
        <span key={`t-${parts.length}`} style={{ color: LOG_JSON_COLORS.punctuation }}>
          {jsonStr.slice(last)}
        </span>
      );
    }
    return parts;
  };

  /** JSON token colors + yellow search marks on the same line (used while filtering). */
  const highlightJsonLineWithSearch = (jsonLine, searchTerm) => {
    const term = (searchTerm || "").trim();
    if (!term) {
      return highlightJsonTokens(jsonLine);
    }
    const parts = [];
    let last = 0;
    let partKey = 0;
    const re = new RegExp(JSON_TOKEN_RE.source, "g");
    let m;
    while ((m = re.exec(jsonLine)) !== null) {
      if (m.index > last) {
        const gap = jsonLine.slice(last, m.index);
        parts.push(
          <React.Fragment key={`g-${partKey++}`}>{highlightSearchTerm(gap, term)}</React.Fragment>
        );
      }
      const match = m[0];
      let color = LOG_JSON_COLORS.number;
      if (/^"/.test(match)) {
        color = /:\s*$/.test(match) ? LOG_JSON_COLORS.key : LOG_JSON_COLORS.string;
      } else if (/true|false|null/.test(match)) {
        color = LOG_JSON_COLORS.keyword;
      }
      parts.push(
        <span key={`t-${partKey++}`} style={{ color }}>
          {highlightSearchTerm(match, term)}
        </span>
      );
      last = re.lastIndex;
    }
    if (last < jsonLine.length) {
      const tail = jsonLine.slice(last);
      parts.push(
        <React.Fragment key={`g-${partKey++}`}>{highlightSearchTerm(tail, term)}</React.Fragment>
      );
    }
    return parts;
  };

  const trySplitLogLineJson = (line) => {
    const brace = line.indexOf("{");
    if (brace === -1) {
      return null;
    }
    const head = line.slice(0, brace);
    const tail = line.slice(brace);
    try {
      const parsed = JSON.parse(tail);
      const expanded = expandEmbeddedJsonStrings(parsed);
      const pretty = JSON.stringify(expanded, null, 2);
      return { head, pretty };
    } catch {
      return null;
    }
  };

  const renderLogLinePlainDecorated = (text) => {
    const tsMatch = text.match(
      /^(\d{2}-\d{2}-\d{4}\s[\d.:]+\s(?:AM|PM))\s+((?:info|error|warn|debug|verbose|http):\s*[^\s{]*)/i
    );
    if (!tsMatch) {
      return text;
    }
    const rest = text.slice(tsMatch[0].length);
    return (
      <>
        <Box component="span" sx={{ color: "#80CBC4", fontWeight: 600 }}>
          {tsMatch[1]}
        </Box>
        <Box component="span" sx={{ mx: 0.75, color: "#FFCC80" }}>
          {tsMatch[2]}
        </Box>
        {rest}
      </>
    );
  };

  /**
   * Pretty-prints trailing JSON when valid (same layout with or without search).
   * With search: keeps filters/highlights; JSON lines use token colors + mark highlights.
   */
  const renderLogLine = (rawLine, lineIndex, searchTerm) => {
    const line = rawLine.replace(/\r/g, "");
    const term = (searchTerm || "").trim();

    const split = trySplitLogLineJson(line);
    if (split) {
      const headEl = term
        ? highlightSearchTerm(split.head, term)
        : renderLogLinePlainDecorated(split.head);

      return (
        <Box
          key={lineIndex}
          component="span"
          sx={{
            display: "block",
            mb: 1.25,
            borderLeft: "3px solid rgba(255,255,255,0.12)",
            pl: 1.5,
            ml: 0.25,
          }}
        >
          <Box component="span" sx={{ color: "#B0BEC5", display: "block", mb: 0.5 }}>
            {headEl}
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 0,
              fontFamily: "inherit",
              fontSize: "inherit",
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: "#E0E0E0",
            }}
          >
            {split.pretty.split("\n").map((jsonLine, j) => (
              <span key={j} style={{ display: "block" }}>
                {term ? highlightJsonLineWithSearch(jsonLine, term) : highlightJsonTokens(jsonLine)}
              </span>
            ))}
          </Box>
        </Box>
      );
    }

    if (term) {
      return (
        <Box key={lineIndex} component="span" sx={{ display: "block", mb: 0.25 }}>
          {highlightSearchTerm(line, term)}
        </Box>
      );
    }

    return (
      <Box key={lineIndex} component="span" sx={{ display: "block", mb: 0.25 }}>
        {renderLogLinePlainDecorated(line)}
      </Box>
    );
  };

  // Filter log content based on search
  useEffect(() => {
    if (!originalContent) {
      return;
    }

    if (!logSearch.trim()) {
      setViewingContent(originalContent);
      return;
    }

    const searchTerm = logSearch.toLowerCase();
    const lines = originalContent.split('\n');
    const filteredLines = lines.filter(line => 
      line.toLowerCase().includes(searchTerm)
    );
    setViewingContent(filteredLines.join('\n'));
  }, [logSearch, originalContent]);

  // Reset search when dialog closes
  useEffect(() => {
    if (!viewDialogOpen) {
      setLogSearch("");
    }
  }, [viewDialogOpen]);

  const cellData = logs?.map((log, index) => {
    const actionEnabled = log.size !== 0;
    return {
      key: log.fileName || index,
      value: [
        isServerLogsMobileCard ? (
          <Box
            key={`log-name-${log.fileName || index}`}
            className="server-logs-mobile-card-primary"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              minWidth: 0,
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                flexShrink: 0,
                mt: 0.125,
                color: colors.borderColor,
                "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
              }}
            >
              {getLogIcon(log.icon)}
            </Box>
            <Typography
              component="div"
              sx={{
                fontWeight: 700,
                fontSize: "0.9375rem",
                lineHeight: 1.35,
                color: "#0f2b46",
                wordBreak: "break-word",
                minWidth: 0,
              }}
            >
              {log.fileName}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getLogIcon(log.icon)}
            <Typography variant="body2">{log.fileName}</Typography>
          </Box>
        ),
        <Box
          key={`type-${log.fileName || index}`}
          className="server-logs-type-cell"
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          <JobberStatusBadge tone={getLogTypeBadgeTone(log.type)}>
            {log.type ?? "—"}
          </JobberStatusBadge>
        </Box>,
        log.sizeFormatted,
        log.time,
        isServerLogsMobileCard ? (
          <Box
            key={`log-actions-${log.fileName || index}`}
            className="server-logs-mobile-actions customer-list-actions-mobile"
            sx={{ display: "grid", width: "100%", maxWidth: "100%" }}
          >
            <Tooltip title={log.size === 0 ? "Log file is empty" : "View log"}>
              <Box component="span" sx={{ display: "block", width: "100%", minWidth: 0 }}>
                <MuiButton
                  type="button"
                  size="small"
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={!actionEnabled}
                  startIcon={
                    <VisibilityIcon
                      sx={{
                        fontSize: "16px !important",
                        color: actionEnabled ? LOG_VIEW_ICON : "#9CA3AF",
                      }}
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!actionEnabled) return;
                    handleViewLog(log);
                  }}
                  sx={{
                    ...serverLogsMobileActionBtnBase,
                    backgroundColor: actionEnabled
                      ? LOG_VIEW_ACTION_BG
                      : LOG_ACTION_DISABLED_BG,
                    color: actionEnabled ? LOG_VIEW_ICON : "#9CA3AF",
                    border: actionEnabled
                      ? LOG_VIEW_ACTION_BORDER
                      : LOG_ACTION_DISABLED_BORDER,
                    "&:hover": {
                      backgroundColor: actionEnabled ? "#DBEAFE" : LOG_ACTION_DISABLED_BG,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: LOG_ACTION_DISABLED_BG,
                      border: LOG_ACTION_DISABLED_BORDER,
                      color: "#9CA3AF",
                    },
                  }}
                >
                  View
                </MuiButton>
              </Box>
            </Tooltip>
            <Tooltip title={log.size === 0 ? "Log file is empty" : "Download log"}>
              <Box component="span" sx={{ display: "block", width: "100%", minWidth: 0 }}>
                <MuiButton
                  type="button"
                  size="small"
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={!actionEnabled}
                  startIcon={
                    <DownloadIcon
                      sx={{
                        fontSize: "16px !important",
                        color: actionEnabled ? LOG_DOWNLOAD_ICON : "#9CA3AF",
                      }}
                    />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!actionEnabled) return;
                    handleDownloadLog(log);
                  }}
                  sx={{
                    ...serverLogsMobileActionBtnBase,
                    backgroundColor: actionEnabled
                      ? LOG_DOWNLOAD_ACTION_BG
                      : LOG_ACTION_DISABLED_BG,
                    color: actionEnabled ? LOG_DOWNLOAD_ICON : "#9CA3AF",
                    border: actionEnabled
                      ? LOG_DOWNLOAD_ACTION_BORDER
                      : LOG_ACTION_DISABLED_BORDER,
                    "&:hover": {
                      backgroundColor: actionEnabled ? "#D1FAE5" : LOG_ACTION_DISABLED_BG,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: LOG_ACTION_DISABLED_BG,
                      border: LOG_ACTION_DISABLED_BORDER,
                      color: "#9CA3AF",
                    },
                  }}
                >
                  Download
                </MuiButton>
              </Box>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Tooltip title={log.size === 0 ? "Log file is empty" : "View Log"}>
              <span>
                <Box
                  onClick={() => {
                    if (!actionEnabled) return;
                    handleViewLog(log);
                  }}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: actionEnabled ? LOG_VIEW_ACTION_BG : LOG_ACTION_DISABLED_BG,
                    border: actionEnabled
                      ? LOG_VIEW_ACTION_BORDER
                      : LOG_ACTION_DISABLED_BORDER,
                    boxShadow: "var(--shadow-sm)",
                    cursor: actionEnabled ? "pointer" : "not-allowed",
                  }}
                >
                  <VisibilityIcon
                    fontSize="small"
                    sx={{ color: actionEnabled ? LOG_VIEW_ICON : "#9CA3AF" }}
                  />
                </Box>
              </span>
            </Tooltip>
            <Tooltip title={log.size === 0 ? "Log file is empty" : "Download Log"}>
              <span>
                <Box
                  onClick={() => {
                    if (!actionEnabled) return;
                    handleDownloadLog(log);
                  }}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: actionEnabled
                      ? LOG_DOWNLOAD_ACTION_BG
                      : LOG_ACTION_DISABLED_BG,
                    border: actionEnabled
                      ? LOG_DOWNLOAD_ACTION_BORDER
                      : LOG_ACTION_DISABLED_BORDER,
                    boxShadow: "var(--shadow-sm)",
                    cursor: actionEnabled ? "pointer" : "not-allowed",
                  }}
                >
                  <DownloadIcon
                    fontSize="small"
                    sx={{ color: actionEnabled ? LOG_DOWNLOAD_ICON : "#9CA3AF" }}
                  />
                </Box>
              </span>
            </Tooltip>
          </Box>
        ),
      ],
    };
  });

  return (
    <Grid className="server-logs">
      <JobberListCard
        title="Log files"
        totalCount={totalCount}
        toolbar={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: "280px" },
                minWidth: 0,
                maxWidth: { sm: "420px" },
                flexShrink: 0,
              }}
            >
              <JobberSearch
                search={search}
                setSearch={(value) => {
                  setSearch(value);
                  setPage(0);
                }}
                placeholder="Search Log Files"
              />
            </Box>

            <Box
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "200px" },
                maxWidth: { xs: "100%", sm: "none" },
                display: "flex",
                alignItems: "center",
                gap: 1,
                overflow: "visible",
                flexShrink: 0,
                "& .MuiStack-root": {
                  paddingTop: "0px !important",
                  margin: "0 !important",
                  alignItems: "center",
                  width: "100%",
                  overflow: "visible",
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  overflow: "visible",
                }}
              >
                {/* Use shared DatePicker defaults (InputFields-style shell); avoid local outline overrides */}
                <DatePicker
                  name="selectedDate"
                  label=""
                  value={selectedDate}
                  format={dateFormat}
                  placeholder={dateFormat}
                  onChange={handleDatePickerChange}
                  fieldHeight={`${SERVER_LOGS_TOOLBAR_H}px`}
                  desktopModeMediaQuery="@media (min-width: 0px)"
                  slotProps={{
                    textField: {
                      sx: { width: "100%", maxWidth: "100%" },
                    },
                  }}
                />
              </Box>

              <Tooltip
                title={
                  logs?.length > 0 ? "Download All Logs" : "No logs to download"
                }
              >
                <span>
                  <IconButton
                    onClick={handleDownloadAll}
                    disabled={!logs?.length}
                    sx={{
                      color:
                        logs?.length > 0
                          ? colors.borderColor
                          : "rgba(0,0,0,0.26)",
                      border: `1px solid ${
                        logs?.length > 0
                          ? colors.borderColor
                          : "rgba(0,0,0,0.12)"
                      }`,
                      borderRadius: "10px",
                      width: SERVER_LOGS_TOOLBAR_H,
                      height: SERVER_LOGS_TOOLBAR_H,
                      padding: "8px",
                      boxSizing: "border-box",
                      bgcolor: "#fff",
                      "&:hover": {
                        backgroundColor:
                          logs?.length > 0 ? "#F7F8FA" : "transparent",
                        border: `1px solid ${
                          logs?.length > 0
                            ? colors.borderColor
                            : "rgba(0,0,0,0.12)"
                        }`,
                      },
                      "&.Mui-disabled": {
                        borderColor: "rgba(0,0,0,0.12)",
                        bgcolor: "#F9FAFB",
                      },
                    }}
                  >
                    <DownloadAllIcon sx={{ fontSize: "1.125rem" }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        }
        loader={loading}
        footer={
          <JobberPagination
            totalData={totalCount}
            currentData={rowsPerPage}
            dataPerPage={rowsPerPage}
            pageItems={[10, 25, 50, 100]}
            page={page}
            setPage={setPage}
            setRowsPerPage={(newRowsPerPage) => {
              setRowsPerPage(newRowsPerPage);
              setPage(0);
            }}
          />
        }
      >
        {loading || logs.length > 0 ? (
          <JobberTable
            className="server-logs-jobber-table"
            style={{ whiteSpace: "nowrap" }}
            showMobileViewDetailsButton={false}
            mobileCardPrimaryColumnIndex={0}
            mobileCardSubtitleColumnIndex={3}
            mobileCardStatusColumnIndex={1}
            headerData={[
              { label: "File Name", field: "fileName" },
              { label: "Type", field: "type", align: "left" },
              { label: "Size", field: "size" },
              { label: "Time", field: "time" },
              { label: "Actions", field: "" },
            ]}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
            sortField={sortField}
            cellData={loading ? [] : cellData}
            isCollapse={false}
            page={page}
            isNavigate={false}
          />
        ) : (
          <Grid
            className="d-flex flex-direction-row justify-content-center align-items-center p-5"
            style={{ justifyContent: "center", textAlign: "center" }}
          >
            <NoDataFound isDonationMode={isDonationMode} />
          </Grid>
        )}
      </JobberListCard>

      {/* View Log Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden" },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            rowGap: 1.25,
            columnGap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <IconButton
            onClick={() => setViewDialogOpen(false)}
            aria-label="Back"
            size="small"
            sx={{
              order: 0,
              flexShrink: 0,
              color: isDonationMode
                ? "var(--bg-donor-color)"
                : "var(--text-blue-color)",
              "& .MuiSvgIcon-root": { color: "inherit" },
            }}
          >
            <ArrowBackIconMain fontSize="small" />
          </IconButton>
          <Tooltip title={viewingLog?.fileName || ""} placement="bottom" enterDelay={400}>
            <Typography
              variant="subtitle1"
              component="span"
              sx={{
                order: 1,
                flex: "1 1 0%",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {viewingLog?.fileName || "Log File"}
            </Typography>
          </Tooltip>
          <IconButton
            onClick={() => setViewDialogOpen(false)}
            aria-label="Close"
            size="small"
            sx={{
              order: { xs: 2, sm: 3 },
              flexShrink: 0,
              ml: { xs: "auto", sm: 0 },
              color: isDonationMode
                ? "var(--bg-donor-color)"
                : "var(--text-blue-color)",
              "& .MuiSvgIcon-root": { color: "inherit" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box
            sx={{
              order: { xs: 4, sm: 2 },
              flex: { xs: "1 1 100%", sm: "0 0 280px" },
              width: { xs: "100%", sm: "auto" },
              minWidth: 0,
              maxWidth: { sm: 360 },
              display: "flex",
              alignItems: "center",
            }}
          >
            <SearchInput
              placeholder="Search logs..."
              value={logSearch}
              onChange={(e) => setLogSearch(e.target.value)}
              fieldHeight="40px"
              fullWidth
            />
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "0px !important",
            backgroundColor: "rgb(49, 49, 49)",
            "&.MuiDialogContent-root": {
              paddingTop: "0px",
              paddingBottom: "0px",
              paddingLeft: "0px",
              paddingRight: "0px",
            },
          }}
        >
          {viewingLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                backgroundColor: "rgb(49, 49, 49)",
              }}
            >
              <WhiteLoaderComponent
                isDonationMode={isDonationMode}
                height="50"
                width="50"
                loader={viewingLoading}
              />
            </Box>
          ) : (
            <Box
              ref={logContentRef}
              sx={{
                width: "100%",
                maxHeight: "70vh",
                overflow: "auto",
                backgroundColor: "rgb(49, 49, 49)",
                color: "#ffffff",
                fontFamily: "monospace",
                fontSize: "12px",
                lineHeight: "1.6",
                padding: "16px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgb(30, 30, 30)",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgb(70, 70, 70)",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "rgb(90, 90, 90)",
                  },
                },
              }}
            >
              {viewingContent ? (
                <Box component="div">
                  {viewingContent.split("\n").map((line, lineIndex) =>
                    renderLogLine(line, lineIndex, logSearch)
                  )}
                </Box>
              ) : (
                "No content available"
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}>
          <WhiteButton
            onClick={() => setViewDialogOpen(false)}
            label="Close"
          />
          {viewingLog && (
            <BlueButton
              onClick={() => handleDownloadLog(viewingLog)}
              label="Download"
              isDonationMode={isDonationMode}
              disabled={viewingLog.size === 0}
            />
          )}
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ServerLogs;

